import * as THREE from "three";
import { TrackballControls } from "three/addons/controls/TrackballControls.js";

const HOME_POS = new THREE.Vector3(0, 38, 118);
const HOME_TARGET = new THREE.Vector3(0, 0, 0);

export function createCameraRig(camera, domElement) {
  const controls = new TrackballControls(camera, domElement);
  controls.rotateSpeed = 1.35;
  controls.zoomSpeed = 1.15;
  controls.panSpeed = 0.75;
  controls.dynamicDampingFactor = 0.18;
  controls.minDistance = 4;
  controls.maxDistance = 280;
  controls.noRoll = false;
  camera.position.copy(HOME_POS);
  controls.target.copy(HOME_TARGET);
  controls.update();

  const followOffset = new THREE.Vector3(0, 3.4, 14.5);
  const look = new THREE.Vector3();
  const desired = new THREE.Vector3();
  const scratch = new THREE.Vector3();
  const idleQuat = new THREE.Quaternion();
  const posFrom = new THREE.Vector3();
  const posTo = new THREE.Vector3();
  const targetFrom = new THREE.Vector3();
  const targetTo = new THREE.Vector3();
  let following = null;
  let followWordPos = new THREE.Vector3();
  let userInteracting = false;
  let animT = -1;
  let animDur = 1.1;

  controls.addEventListener("start", () => {
    userInteracting = true;
    animT = -1;
  });
  controls.addEventListener("end", () => {
    userInteracting = false;
  });

  function follow(index, wordPosition) {
    following = index;
    followWordPos.copy(wordPosition);
    controls.enabled = false;
    animT = -1;
    const away = camera.position.clone().sub(controls.target);
    if (away.length() < 0.01) away.set(0, 3, 14);
    away.setLength(14.5);
    followOffset.copy(away);
    if (Math.abs(followOffset.y) < 1.2) followOffset.y = 3.2;
  }

  function clearFollow() {
    if (following === null) return;
    controls.target.copy(followWordPos);
    controls.enabled = true;
    following = null;
    controls.update();
  }

  function isFollowing() {
    return following !== null;
  }

  function followedIndex() {
    return following;
  }

  function startMove(nextPos, nextTarget, duration = 1.1) {
    following = null;
    controls.enabled = true;
    posFrom.copy(camera.position);
    targetFrom.copy(controls.target);
    posTo.copy(nextPos);
    targetTo.copy(nextTarget);
    animT = 0;
    animDur = duration;
  }

  function resetOverview() {
    startMove(HOME_POS, HOME_TARGET, 1.2);
  }

  function dolly(factor) {
    if (following !== null) {
      if (factor > 1) {
        resetOverview();
        return;
      }
      followOffset.multiplyScalar(factor);
      const len = followOffset.length();
      if (len < 6) followOffset.setLength(6);
      if (len > 42) followOffset.setLength(42);
      return;
    }
    animT = -1;
    scratch.copy(camera.position).sub(controls.target);
    const next = Math.min(controls.maxDistance, Math.max(controls.minDistance, scratch.length() * factor));
    scratch.setLength(next);
    camera.position.copy(controls.target).add(scratch);
    controls.update();
  }

  function update(dt, wordPosition) {
    if (animT >= 0) {
      animT += dt;
      const u = Math.min(1, animT / animDur);
      const s = u * u * (3 - 2 * u);
      camera.position.lerpVectors(posFrom, posTo, s);
      controls.target.lerpVectors(targetFrom, targetTo, s);
      camera.up.lerp(scratch.set(0, 1, 0), 0.2).normalize();
      camera.lookAt(controls.target);
      controls.update();
      if (u >= 1) animT = -1;
      return;
    }

    if (following !== null && wordPosition) {
      followWordPos.copy(wordPosition);
      followOffset.applyAxisAngle(scratch.set(0, 1, 0), dt * 0.085);
      desired.copy(wordPosition).add(followOffset);
      camera.position.lerp(desired, 1 - Math.pow(0.0008, dt));
      look.copy(wordPosition);
      camera.lookAt(look);
      camera.up.lerp(scratch.set(0, 1, 0), 0.02).normalize();
      return;
    }

    if (!userInteracting) {
      idleQuat.setFromAxisAngle(scratch.set(0, 1, 0), dt * 0.035);
      camera.position.sub(controls.target).applyQuaternion(idleQuat).add(controls.target);
    }
    controls.update();
  }

  function flyTo(target, distance = 28) {
    const dir = camera.position.clone().sub(controls.target);
    if (dir.lengthSq() < 0.001) dir.set(0, 0.35, 1);
    dir.normalize().multiplyScalar(distance);
    startMove(target.clone().add(dir), target, 0.85);
  }

  function resize() {
    controls.handleResize();
  }

  function dispose() {
    controls.dispose();
  }

  return {
    controls,
    follow,
    clearFollow,
    isFollowing,
    followedIndex,
    update,
    flyTo,
    resetOverview,
    dolly,
    resize,
    dispose,
  };
}
